import React, { memo } from 'react';
import { preventNonNumericValue } from 'utils/helpers';
import { ValidationErrors } from 'constants/validations';
import { CloseIcon, DoneIcon } from 'components/common/icons';
import { EqualIcon } from 'components/common/icons/equalIcon';
import { Button, Col, Form, FormModal, Input, InputNumber, PriceRow, Row, Select, Tag, Typography } from 'components';
import {
  SHIPPING_MEASUREMENT_RANGE_KG,
  SHIPPING_MEASUREMENT_RANGE_VOLUME,
  SHIPPING_METHOD_OPTIONS,
  SHIPPING_RATE_METRICS,
  SHIPPING_SPEED_OPTIONS,
} from 'staticData/form/client/shipping';
import { useRatesAddEditModal } from './useRatesAddEditModal';
import { SupplierSpecialRate } from '..';

const { Item } = Form;
const { TextArea } = Input;

export interface IRatesAddEditModal {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: SupplierSpecialRate;
}

export const RatesAddEditModal = memo<IRatesAddEditModal>((props) => {
  const { modalState, dataToEdit, onModalStateChange } = props;
  const {
    onFinish,
    onCancel,
    onPriceRowRemove,
    onPriceRowEdit,
    onPriceSave,
    onPriceRowSave,
    onPriceRowCancel,
    onPriceCancel,
    warehouseData,
    form,
    prices,
    triggerValidation,
    editId,
    isChanged,
    isLoading,
    disableSaveBtn,
    type,
    handleTypeChange,
    clearFieldValidation,
  } = useRatesAddEditModal({
    modalState,
    dataToEdit,
    onModalStateChange,
  });
  return (
    <FormModal
      open={modalState}
      disableOkBtn={disableSaveBtn}
      isLoading={isLoading}
      form={form}
      title={`${dataToEdit ? 'Update' : 'Add'} Shipping Rate`}
      okText={dataToEdit ? 'Save' : 'Add'}
      width={680}
      onCancel={onCancel}
      onSubmit={onFinish}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Item
            name="warehouseId"
            label={'To*'}
            rules={[
              {
                required: true,
                message: ValidationErrors.required('To'),
              },
            ]}
          >
            <Select options={warehouseData} size={'large'} />
          </Item>
        </Col>
        <Col span={12}>
          <Item
            name="shippingMethod"
            label={'Shipping Method*'}
            rules={[
              {
                required: true,
                message: ValidationErrors.required('Shipping Method'),
              },
            ]}
          >
            <Select options={SHIPPING_METHOD_OPTIONS} size={'large'} />
          </Item>
        </Col>
        <Col span={12}>
          <Item name="shippingSpeed" label={'Shipping Speed'}>
            <Select size={'large'} options={SHIPPING_SPEED_OPTIONS} />
          </Item>
        </Col>
        <Col span={12}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Item
                label={'Lead Time'}
                name={'leadTimeMin'}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      if (!/^[1-9]\d*$/.test(String(value))) {
                        return Promise.reject(new Error('Minimum lead time must be a positive integer.'));
                      }
                      const maxLeadTime = getFieldValue('maxLeadTime');
                      if (maxLeadTime && value > maxLeadTime) {
                        return Promise.reject(new Error('Minimum lead time must be less than Maximum.'));
                      }
                      clearFieldValidation(form, 'maxLeadTime');
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  placeholder={'Min'}
                />
              </Item>
            </Col>
            <Col span={12}>
              <Item
                label={<span style={{ display: 'none' }}></span>}
                name={'leadTimeMax'}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      if (!/^[1-9]\d*$/.test(String(value))) {
                        return Promise.reject(new Error('Maximum lead time must be a positive integer.'));
                      }
                      const minLeadTime = getFieldValue('minLeadTime');
                      if (minLeadTime !== undefined && value < minLeadTime) {
                        return Promise.reject(new Error('Maximum lead time must be more than Minimum.'));
                      }
                      clearFieldValidation(form, 'minLeadTime');
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber<number>
                  controls={false}
                  size={'large'}
                  onBeforeInput={(event: any) => preventNonNumericValue(event)}
                  style={{ width: '100%' }}
                  suffix={
                    <Row className={'gap-4'}>
                      <Tag
                        className={`input-small-tag ${type === 'DAY' && 'selected-input-small-tag'}`}
                        onClick={() => handleTypeChange('DAY')}
                      >
                        Day
                      </Tag>
                      <Tag
                        className={`input-small-tag ${type === 'WEEK' && 'selected-input-small-tag'}`}
                        onClick={() => handleTypeChange('WEEK')}
                      >
                        Week
                      </Tag>
                    </Row>
                  }
                  placeholder={'Max'}
                />
              </Item>
            </Col>
          </Row>
        </Col>
        <Col span={24} className={'margin-t-8'}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Row justify={'space-between'} align={'middle'} gutter={[0, 16]}>
                <Col>
                  <Typography.Text style={{ color: '#05796B', fontSize: '20px' }}>Price</Typography.Text>
                </Col>
                {!!prices.length && (
                  <Col span={24}>
                    <Row gutter={[0, 6]}>
                      {prices.map((_price) => (
                        <PriceRow
                          key={_price.measurementRange}
                          priceList={prices}
                          data={_price}
                          isEditeMode={_price.measurementRange === editId}
                          onPriceRowRemove={onPriceRowRemove}
                          onPriceRowEdit={onPriceRowEdit}
                          onPriceRowSave={onPriceRowSave}
                          onPriceRowCancel={onPriceRowCancel}
                        />
                      ))}
                    </Row>
                  </Col>
                )}
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={[8, 0]} align={'top'} justify={'space-between'} className={'margin-t-6'}>
                <Col span={8}>
                  <Item
                    name="metrics"
                    label={'Metrics'}
                    rules={[
                      {
                        required: triggerValidation || !prices.length,
                        message: ValidationErrors.required('Metrics'),
                      },
                    ]}
                  >
                    <Select
                      onChange={() => form.resetFields(['measurementRange'])}
                      options={SHIPPING_RATE_METRICS}
                      size={'large'}
                    />
                  </Item>
                </Col>
                <Col span={8}>
                  <Item
                    shouldUpdate={(prevValues: any, currentValues: any) => prevValues.metrics !== currentValues.metrics}
                  >
                    {({ getFieldValue }) => {
                      const measurementRangeOptions =
                        getFieldValue('metrics') === 'KG'
                          ? SHIPPING_MEASUREMENT_RANGE_KG
                          : SHIPPING_MEASUREMENT_RANGE_VOLUME;
                      return (
                        <Item
                          name="measurementRange"
                          label={'Measurement Range'}
                          rules={[
                            {
                              required: triggerValidation || !prices.length,
                              message: ValidationErrors.required('Measurement Range'),
                            },
                          ]}
                        >
                          <Select
                            options={measurementRangeOptions.map((i: { value: string; label: string }) => ({
                              ...i,
                              disabled: prices.some((price) => price.measurementRange === i.value),
                            }))}
                            size={'large'}
                            disabled={!getFieldValue('metrics')}
                          />
                        </Item>
                      );
                    }}
                  </Item>
                </Col>
                <Col style={{ paddingTop: 32 }}>
                  <EqualIcon />
                </Col>
                <Col flex={'2'}>
                  <Item
                    name="price"
                    label={'Price'}
                    rules={[
                      {
                        required: triggerValidation || !prices.length,
                        message: ValidationErrors.required('Price'),
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }
                          const regex = /^\d+(\.\d{1,2})?$/;
                          if (!regex.test(value) || parseFloat(value) <= 0) {
                            return Promise.reject(
                              new Error('Price must be a positive number with up to 2 decimal points.')
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber<number>
                      controls={false}
                      size={'large'}
                      onBeforeInput={(event: any) => preventNonNumericValue(event)}
                      style={{ width: '100%' }}
                      suffix={<Typography.Text type={'secondary'}>€</Typography.Text>}
                    />
                  </Item>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row gutter={4} justify={'end'}>
                <Col>
                  <Button
                    disabled={!isChanged}
                    color="default"
                    variant="filled"
                    onClick={onPriceCancel}
                    icon={<CloseIcon />}
                  />
                </Col>
                <Col>
                  <Button onClick={onPriceSave} disabled={!isChanged} type={'primary'} icon={<DoneIcon />} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24} className={'margin-t-8'}>
          <Item name="specialHandling" label={'Special Handling'}>
            <TextArea style={{ resize: 'none' }} rows={5} size={'large'} />
          </Item>
        </Col>
      </Row>
    </FormModal>
  );
});
